import { api } from './client'
import type { Pool } from './pools'
import type { paths } from './schema'

// The market catalog (config/catalog.yml): common filament brands, lines and colors, and printer
// brands and models, for the Pool form (M-02) and the Machine form (F-02). It changes only with a
// deploy, so it is fetched once per session.
export type Catalog = paths['/api/w/{slug}/catalog']['get']['responses'][200]['content']['application/json']
type Printers = Catalog['printers']

export function useCatalog(slug: string) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/catalog',
    { params: { path: { slug } } },
    { retry: false, staleTime: Infinity },
  )
}

const unique = (values: (string | null | undefined)[]) =>
  [...new Set(values.filter((value): value is string => Boolean(value)))]

// The choices for each filament key field, given the fields above it: the catalog's, then the
// values already used in this Workspace (#62), so a typed brand or color is offered next time.
// A brand outside the catalog still gets the catalog's material types; a line without its own
// colors gets the shared color list. `colorHex` is the swatch per color name: the line's own, else
// the shared list's (so a Workspace color named like a shared one gets its swatch too).
export function filamentOptions(
  catalog: Catalog | undefined,
  pools: Pool[] | undefined,
  { brand, material_type, product_line }: Pick<Pool, 'brand' | 'material_type' | 'product_line'>,
) {
  const brands = catalog?.filament.brands ?? []
  const lines = brands.find((entry) => entry.name === brand)?.lines ?? []
  const typeLines = lines.filter((line) => line.material_type === material_type)
  const line = typeLines.find((entry) => entry.product_line === product_line)
  const used = (pools ?? []).filter((pool) => pool.kind === 'filament' && pool.brand === brand)
  const usedType = used.filter((pool) => pool.material_type === material_type)
  const sharedColors = catalog?.filament.colors ?? []
  const lineColors = line?.colors.length ? line.colors : sharedColors

  return {
    brands: unique([
      ...brands.map((entry) => entry.name),
      ...(pools ?? []).filter((pool) => pool.kind === 'filament').map((pool) => pool.brand),
    ]),
    unbranded: brands.filter((entry) => entry.unbranded).map((entry) => entry.name),
    materialTypes: unique([
      ...(lines.length > 0 ? lines : brands.flatMap((entry) => entry.lines)).map((entry) => entry.material_type),
      ...used.map((pool) => pool.material_type),
    ]),
    productLines: unique([...typeLines.map((entry) => entry.product_line), ...usedType.map((pool) => pool.product_line)]),
    colors: unique([
      ...lineColors.map((color) => color.name),
      ...usedType.filter((pool) => pool.product_line === product_line).map((pool) => pool.color),
    ]),
    colorHex: new Map([...sharedColors, ...lineColors].map((color) => [color.name, color.hex])),
  }
}

// A Machine stores "<brand> <model>" in `model`. Split it back into the catalog brand and the
// rest; a value that starts with no catalog brand comes back whole as the brand (typed).
export function splitPrinterModel(model: string | null | undefined, printers: Printers) {
  const value = model ?? ''
  const match = printers.find((printer) => value === printer.brand || value.startsWith(`${printer.brand} `))
  return match ? { brand: match.brand, model: value.slice(match.brand.length).trim() } : { brand: value, model: '' }
}

export const joinPrinterModel = (brand: string, model: string) =>
  [brand.trim(), model.trim()].filter(Boolean).join(' ')
